import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export interface DropdownOption {
  label: string;
  value: string;
}

/**
 * A reusable simple dropdown component that displays a list of options, manages selection and open/close state, and emits events for selection and visibility changes.
 * @component
 * @selector app-simple-dropdown
 * @example
 *   <app-simple-dropdown
 *     [options]="items"
 *     [selectedValue]="currentValue"
 *     [placeholder]="'Choose...'"
 *     [title]="'Options'"
 *     [disabled]="isDisabled"
 *     [isOpen]="isOpen"
 *     (selectionChange)="onSelectionChange($event)"
 *     (toggle)="onToggle()"
 *     (close)="onClose()">
 *   </app-simple-dropdown>
 *
 * Purpose:
 *   Provides a lightweight, standalone dropdown for selecting a single value from a provided array of options. Handles outside clicks to close, emits selection and visibility events, and exposes helpers to read labels and selection state.
 *
 * Inputs:
 * @input {DropdownOption[]} options - The list of dropdown options, each expected to have { value: string, label: string }.
 * @input {string} selectedValue - The currently selected value; used to determine selection state and displayed label.
 * @input {string} placeholder - Text to display when no option is selected (default: 'Select option').
 * @input {string} title - Accessible title or heading for the dropdown (default: 'Options').
 * @input {boolean} disabled - When true, prevents opening/toggling and selection.
 * @input {boolean} isOpen - Controlled open state for the dropdown; can be used to programmatically open/close.
 *
 * Outputs:
 * @output {EventEmitter<string>} selectionChange - Emitted when an option is selected; payload is the selected value.
 * @output {EventEmitter<void>} toggle - Emitted when a user attempts to toggle the dropdown open/closed (only if not disabled).
 * @output {EventEmitter<void>} close - Emitted when the dropdown should be closed (e.g., outside click or after selection).
 *
 * Key methods:
 * @method toggleDropdown - Emits the toggle event to request opening or closing the dropdown; guarded by the disabled flag.
 * @method selectOption - Sets the selectedValue, emits close and selectionChange events with the chosen value.
 * @method getSelectedLabel - Returns the label for the current selectedValue, or the placeholder if none is selected.
 * @method isSelected - Returns true if a given value matches the current selectedValue.
 * @method onDocumentClick - Host listener for document clicks; emits close when a click occurs outside the component.
 *
 * Lifecycle and providers:
 *   Injects ElementRef in the constructor to detect outside clicks. No explicit Angular lifecycle hooks (ngOnInit, ngOnDestroy) are required by this component, but outside-click handling is provided via a HostListener on the document.
 */
@Component({
  selector: 'app-simple-dropdown',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './simple-dropdown.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./simple-dropdown.component.scss'],
})
export class SimpleDropdownComponent implements OnChanges, OnDestroy {
  @Input() options: DropdownOption[] = [];
  @Input() selectedValue = '';
  @Input() placeholder = 'Select option';
  @Input() title = 'Options';
  @Input() disabled = false;
  @Input() isOpen = false;

  @Output() selectionChange = new EventEmitter<string>();
  @Output() toggle = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  menuStyle: Record<string, string> = {};
  openUpward = false;

  private readonly boundReposition = () => this.updateMenuPosition();
  private readonly boundDocumentClick = (event: Event) => this.onDocumentClick(event);

  constructor(private elementRef: ElementRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['isOpen']) {
      return;
    }

    if (this.isOpen) {
      document.addEventListener('scroll', this.boundReposition, true);
      window.addEventListener('resize', this.boundReposition);
      document.addEventListener('click', this.boundDocumentClick, true);
      requestAnimationFrame(() => this.updateMenuPosition());
      return;
    }

    this.removeDocumentListeners();
    this.menuStyle = {};
    this.openUpward = false;
  }

  ngOnDestroy(): void {
    this.removeDocumentListeners();
  }

  private removeDocumentListeners(): void {
    document.removeEventListener('scroll', this.boundReposition, true);
    window.removeEventListener('resize', this.boundReposition);
    document.removeEventListener('click', this.boundDocumentClick, true);
  }

  private updateMenuPosition(): void {
    if (!this.isOpen) {
      return;
    }

    const root = this.elementRef.nativeElement as HTMLElement;
    const button = root.querySelector('.dropdown-button') as HTMLElement | null;
    const menu = root.querySelector('.dropdown-menu') as HTMLElement | null;
    if (!button || !menu) {
      return;
    }

    const buttonRect = button.getBoundingClientRect();
    const menuHeight = menu.offsetHeight;
    const gap = 8;
    const viewportPadding = 8;
    const spaceBelow = window.innerHeight - buttonRect.bottom - gap;
    const spaceAbove = buttonRect.top - gap;
    this.openUpward = menuHeight > spaceBelow && spaceAbove >= spaceBelow;

    let top = this.openUpward ? buttonRect.top - menuHeight - gap : buttonRect.bottom + gap;

    top = Math.max(
      viewportPadding,
      Math.min(top, window.innerHeight - menuHeight - viewportPadding)
    );

    // Size the menu to its widest option so labels are not clipped by the
    // trigger's width, but keep it at least as wide as the trigger and inside
    // the viewport. Labels are measured directly (they never wrap): the
    // .dropdown-options wrapper is a scroll container, so overflowing labels
    // would not show up in the menu's own scrollWidth.
    const optionsEl = menu.querySelector('.dropdown-options') as HTMLElement | null;
    const scrollbarWidth = optionsEl ? optionsEl.offsetWidth - optionsEl.clientWidth : 0;
    let contentWidth = buttonRect.width;
    const labels = menu.querySelectorAll<HTMLElement>('.option-label');
    if (labels.length > 0) {
      // Horizontal chrome around a label (menu border + paddings), assumed
      // symmetric, derived from the rendered layout so SCSS stays the source
      // of truth for the actual values.
      const chrome =
        2 * (labels[0].getBoundingClientRect().left - menu.getBoundingClientRect().left);
      let widestLabel = 0;
      labels.forEach(label => {
        widestLabel = Math.max(widestLabel, label.getBoundingClientRect().width);
      });
      contentWidth = Math.ceil(widestLabel + chrome + scrollbarWidth);
    }
    const menuWidth = Math.min(
      Math.max(contentWidth, buttonRect.width),
      window.innerWidth - viewportPadding * 2
    );
    const left = Math.max(
      viewportPadding,
      Math.min(buttonRect.left, window.innerWidth - menuWidth - viewportPadding)
    );

    this.menuStyle = {
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      width: `${menuWidth}px`,
      zIndex: '20001',
    };
  }

  private onDocumentClick(event: Event): void {
    if (!this.isOpen) {
      return;
    }

    const target = event.target as Node | null;
    if (target && !this.elementRef.nativeElement.contains(target)) {
      this.close.emit();
    }
  }

  toggleDropdown(): void {
    if (!this.disabled) {
      this.toggle.emit();
    }
  }

  selectOption(value: string): void {
    this.selectedValue = value;
    this.close.emit();
    this.selectionChange.emit(value);
  }

  getSelectedLabel(): string {
    // Match by value first so options with empty values (e.g. "All Lanes") still resolve.
    const option = this.options?.find(opt => opt.value === this.selectedValue);
    if (option) {
      return option.label;
    }
    if (!this.selectedValue) {
      return this.placeholder;
    }
    return this.selectedValue;
  }

  isSelected(value: string): boolean {
    return this.selectedValue === value;
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
